import React from "react";
import { Column } from "../components/Layout";
import { Signature } from "../components/Signature";
import { FadeIn } from "../components/FadeIn";
import { OutlineLink } from "../components/OutlineLink";
import { Seo } from "../components/Seo";

const FAQS = [
  {
    q: "What exactly is a Raven's Quill biography?",
    a: "It is a full-length work of anthropological portraiture: the study of a single life, authored by a writer-anthropologist using the methods used to understand a culture. It is not a ghostwritten memoir and not an as-told-to autobiography. It is an independent, deeply researched account of a life, written with the subject rather than for them, and bound as an heirloom made to be opened for generations.",
  },
  {
    q: "What is The Final Tribute?",
    a: "It is the press's second practice: a seventy-two-hour anthropological commission undertaken in the days between death and farewell. Within the time that ritual allows, we gather the memories, photographs, and voices that remain, and write a tribute faithful to the life that has just ended, delivered ready for public reading and for inclusion in the funeral booklet or memorial programme. It begins with a telephone call or WhatsApp message.",
  },
  {
    q: "Do you write eulogies and obituaries?",
    a: "Yes, within The Final Tribute. Families most often arrive looking for a eulogy writer, or for an obituary worthy of the funeral programme. What the practice provides is those things, made anthropologically: seventy-two hours of listening to the people, photographs, and voices that remain, and then a tribute written for public delivery, with print-ready copy for the funeral booklet. It begins with a telephone call or WhatsApp message.",
  },
  {
    q: "How is this different from hiring a ghostwriter?",
    a: "A ghostwriter records what a subject chooses to say and writes in the subject's own voice, producing a book the subject authors in all but labour. The Raven's Quill does the opposite. The anthropologist studies the life from the outside in, through archives, a residency, and the circle around the subject, and writes as the author of a study. Two presences remain visible on the page: the subject's voice, and the observer's lens. The result is truer, more textured, and free of the quiet vanity that stops so many distinguished people from being written about at all.",
  },
  {
    q: "What is it like to be the subject of the work?",
    a: "Gentler than most people expect, and more enjoyable. The first months are conversations: one a week, an hour or two, recorded, at whatever time of day suits you, in which you tell and the anthropologist listens. The pace is yours throughout. You may rest, skip a week, or leave a subject for another day at any point, and nothing is ever demanded beyond your own company. The residency follows. Then, with your blessing, come conversations with the people you choose, and only the people you choose; the anthropologist approaches no one before you have. And when the writing is done, you read every chapter before anyone else in the world does. Nothing you are not happy with goes in. The deadline moves before your say-so ever does.",
  },
  {
    q: "My mother is in her eighties. Is the process too demanding for her?",
    a: "The method was built for precisely this stage of life. The heaviest labour, the archive, the research, the writing, is carried entirely by the anthropologist. What is asked of the subject is the one thing she is uniquely qualified to do: talk about her life, at her own pace, to someone who has come to listen. Sessions are short, rest is built in, and any week can be skipped without consequence to the work. Families often find that the conversations become something looked forward to in their own right, because a weekly hour of undivided listening is a rarity the method happens to provide. Where health or energy require it, the rhythm bends. The book waits for the person, never the reverse.",
  },
  {
    q: "Why is there no price on the website?",
    a: "Because there is no standard product to price. Each commission differs in scope, in the length and complexity of the life, in the depth of the archive, in whether the work extends beyond the book into other formats. A price is therefore established privately, after a first consultation, once the shape of the work is understood. We quote the commission, not a catalogue. What we can say plainly is that this is an ultra-premium engagement, undertaken for only a few lives at a time.",
  },
  {
    q: "Who owns the finished book?",
    a: "The family holds the physical volumes of the heritage run, a press-ready master, and a perpetual licence to reprint the volume privately, at cost, for as long as the lineage wishes. The subject holds an absolute veto over anything private, and the press undertakes in writing that the work is never published or shown without the family's consent. What remains with The Raven's Quill is the authorship and copyright of the work itself, as a scholar retains the authorship of a study. This is a condition of the practice rather than a term of negotiation: it is what keeps each volume a true study rather than a commissioned memoir, and what allows the press to hold every life it records within a single, permanent archive. Authorship is never sold. Nothing is ever taken from you.",
  },
  {
    q: "If the press holds the copyright, do I lose control of my story?",
    a: "No. Authorship and control of privacy are two different things, and the second belongs entirely to you. You hold a strict veto over intimate or confidential material, dedicated months of the engagement are ring-fenced for you to read and challenge the work while it is still soft, and the press undertakes in writing that the work is never published, displayed, or used without your consent.",
  },
  {
    q: "Is the work confidential?",
    a: "Entirely, until you approve the final manuscript or unless you decide otherwise during the commission. Discretion is the default at every stage. Original papers, letters, and photographs remain your property and are returned on completion. What is shared in confidence stays in confidence. Only a few commissions are undertaken at a time precisely so this standard is never compromised.",
  },
  {
    q: "Why only a few commissions at a time?",
    a: "Because the method does not divide. A residency cannot be lived in a dozen houses at once, and a writer can hold only so many lives in the state of attention this work demands. The practice therefore undertakes only a few Life Studies at a time, each carried from the first conversation to the delivered book, and commissions are allocated from a waiting list. A place on it is arranged in the first consultation.",
  },
  {
    q: "How long does a commission take?",
    a: "A full commission runs across roughly a year. Its strands overlap rather than run in sequence: the weekly recorded conversations with the subject, the gathering and reading of the archive, a residency within the subject's world, recorded encounters with the circle around them, and the composition of the volume. Lives of unusual scope, or commissions extended into other formats, take longer. The work is unhurried by design. Depth cannot be rushed without becoming something lesser.",
  },
  {
    q: "What is actually delivered?",
    a: "A full literary volume, with restored photographs woven into the narrative. A limited heritage run of sewn, hardcover books, bound to museum standard, one volume for each year of the life, for the people you choose to receive them. And a press-ready master, held by the family permanently. A commission may also be extended into a documentary, a sonic archive of the voice, or a secured digital vault of the research.",
  },
  {
    q: "Can I simply receive the digital files instead of a printed book?",
    a: "The book is the work, not a by-product of it. We make a physical object on purpose. In a world where the digital is the default, an object that must be carried, opened, and handed on by hand transmits something a weightless file cannot: the fact that a life was gathered, weighed, and held to matter. A secured digital archive can accompany the work for those who wish it, but the bound volume is its heart.",
  },
  {
    q: "Do you only write about people who are still living?",
    a: "Most commissions are for a living subject, whose presence and participation are part of the method. A life recently ended can also be preserved, through its archive and the memory of the circle around it, where enough remains to study honestly. This is best discussed directly, as it depends on what the record allows.",
  },
  {
    q: "Is there a launch event?",
    a: "The finished heritage run is delivered on a date of your choosing. What happens on that day is yours to shape. Some receive the work quietly; others time delivery to a meaningful occasion, a landmark birthday among them, and share the volumes with those closest to them. The Raven's Quill does not stage the occasion. It places the finished work in your hands.",
  },
  {
    q: "How do I begin?",
    a: "With a conversation, not a commitment. A first private consultation establishes the life in question and whether this is the right way to preserve it. Should both sides wish to continue, a full prospectus follows, detailing the method, the timeline, the deliverables, and the investment. Only then is a commission confirmed.",
  },
];

const slug = (s) =>
  s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

const Faq = () => {
  return (
    <div data-testid="faq-page">
      <Seo
        title="Questions, Answered Plainly · The Raven's Quill"
        description="Honest answers to the questions most often asked about commissioning an anthropological biography."
        path="/faq"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQS.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }}
      />

      <section className="pt-20 pb-16 md:pt-28 md:pb-20">
        <Column>
          <Signature label="Frequently Asked" testid="faq-signature" />
          <FadeIn>
            <h1
              className="text-[24px] md:text-[26px] italic leading-snug text-[#1C1A17]"
              data-testid="faq-headline"
            >
              Questions, Answered Plainly
            </h1>
          </FadeIn>
          <FadeIn>
            <p
              className="text-[17px] md:text-[18px] leading-[1.8] text-[#1C1A17]"
              data-testid="faq-intro"
            >
              A commission is a considered decision, and considered decisions deserve
              honest answers. What follows are the questions we are most often asked,
              addressed as directly as the work allows. Anything not covered here is
              best explored in a private conversation.
            </p>
          </FadeIn>

          <div className="mt-14 space-y-12">
            {FAQS.map((item) => (
              <FadeIn key={item.q} data-testid={`faq-item-${slug(item.q)}`}>
                <h3 className="text-[19px] md:text-[21px] italic leading-snug text-[#1C1A17]">
                  {item.q}
                </h3>
                <hr
                  className="mt-4 mb-5 border-0"
                  style={{ width: "48px", height: "0.75px", backgroundColor: "#7A2E2E" }}
                />
                <p className="text-[17px] md:text-[18px] leading-[1.8] text-[#1C1A17]">
                  {item.a}
                </p>
              </FadeIn>
            ))}
          </div>

          <FadeIn className="mt-16">
            <p
              className="text-center text-[18px] md:text-[20px] italic leading-[1.6] text-[#1C1A17]"
              data-testid="faq-closing"
            >
              If your question is not here, it is likely one worth asking in person.
            </p>
          </FadeIn>

          <FadeIn className="mt-12 flex justify-center">
            <OutlineLink to="/commission" testid="faq-cta">
              Request a Private Consultation
            </OutlineLink>
          </FadeIn>
        </Column>
      </section>
    </div>
  );
};

export default Faq;
